import { useContext, useMemo } from "react"
import { useAppSelector } from "@/reducers/hooks"
import { type Message as MessageType } from "@/reducers/messagesSlice"
import {
  findAuthorProfile,
  messageAuthorName,
} from "@/reducers/profilesSliceSelectors"
import { selectShowPhoneWithName } from "@/reducers/serviceConfigSlice"
import {
  selectActiveChat,
  selectDriverParticipant,
  selectChat,
} from "@/reducers/chatsSliceSelectors"
import { participantPhotoToSrc } from "@/components/AvatarImage"
import MessageHelpers from "@/lib/messageHelpers"
import { XmppContext } from "@/stream/xmppClient"
import ChatHelpers from "@/lib/chatHelpers"
import { selectAccountChatByJid } from "@/reducers/accountChatsSlice"
import { getTransicsMessagePresentation } from "@/lib/transicsMessagePresentation"
import { ArchivedChatContext } from "@/contexts/ArchivedChatContext"
import { buildChat } from "@/reducers/chatsSlice"
import { isMucJid } from "@/jidUtils"

export type VariantType = "incoming" | "outgoing" | "ai" | null | undefined

export const useMessageData = (message: MessageType) => {
  const { myJid } = useContext(XmppContext)
  const liveChat = useAppSelector(selectActiveChat)
  const archive = useContext(ArchivedChatContext)
  const archiveJid = archive?.chatJid
  const archiveChat = useAppSelector(selectChat(archiveJid ?? ""))
  const activeChat = useMemo(
    () =>
      archiveJid
        ? (archiveChat ??
          buildChat({ jid: archiveJid, room: isMucJid(archiveJid) }))
        : liveChat,
    [archiveJid, archiveChat, liveChat],
  )

  const chatJid = archive?.chatJid ?? activeChat.jid

  const accountChat = useAppSelector(selectAccountChatByJid(chatJid))
  const transicsPresentation = getTransicsMessagePresentation(
    message,
    accountChat,
  )
  const isDoubleCrewChat = accountChat?.group === "double_crew"
  const authorProfile = useAppSelector(
    findAuthorProfile(message, myJid.toString()),
  )
  const authorName = useAppSelector(messageAuthorName(message, myJid))
  const showPhoneWithName = useAppSelector(selectShowPhoneWithName)
  // `authorLabel` is the string shown in message headers.  When the vhost
  // config enables phone display and the profile carries a phone number, we
  // append it with a middle-dot separator.  `authorName` stays phone-free so
  // avatar initials are not polluted.
  const authorLabel =
    showPhoneWithName && authorProfile?.phone && authorProfile.role === "office"
      ? `${authorName} (${authorProfile.phone})`
      : authorName
  const authorInitials = authorName
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  const driverParticipant = useAppSelector(selectDriverParticipant(chatJid))

  const isIncoming = MessageHelpers.isIncoming(message)
  const driverJid =
    isDoubleCrewChat || accountChat?.driverA
      ? accountChat.jid
      : driverParticipant?.jid
  const isFromDriver = MessageHelpers.hasComeFromJid(message, driverJid)
  const avatarSrc = participantPhotoToSrc(authorProfile?.photo)

  const reactions = MessageHelpers.reactionsForDisplay(
    message,
    myJid,
    driverJid,
  )

  const avatarColor = useMemo(() => {
    // const chatColor = ChatHelpers.getChatColor(activeChat)
    // if (chatColor) return chatColor

    // // Fallback colors based on chat state
    // if (isArchived) return "#cccccc"
    // if (accountChats[jid]?.group === "shared_driver") return "#ffecb8"

    return isIncoming && isFromDriver
      ? "bg-blue-100 dark:bg-blue-950"
      : "bg-secondary text-accent-foreground"
  }, [isFromDriver, isIncoming])

  const fromSlickAI = ChatHelpers.isMessageFromSlickAI(message.from)

  return {
    activeChat,
    accountChat,
    isDoubleCrewChat,
    authorProfile,
    authorName,
    authorLabel,
    displayBody: transicsPresentation?.body,
    displayBodyOffset: transicsPresentation?.bodyOffset,
    authorInitials,
    avatarColor,
    driverParticipant,
    isIncoming,
    isFromDriver,
    avatarSrc,
    reactions,
    variant: (isIncoming ? "incoming" : "outgoing") as VariantType,
    fromSlickAI,
  }
}
